import React, { useEffect, useRef, useState } from 'react';
import {
  UserAddOutlined,
  FileAddOutlined,
  SendOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext';
import { SEOHead } from '../components/SEO/SEOHead';
import { StructuredData } from '../components/SEO/StructuredData';
import ScrollSmootherComponent from '../components/ScrollSmoother';
import PublicHeader from '../components/landing/PublicHeader';
import PublicFooter from '../components/landing/PublicFooter';
import PageHero from '../components/landing/PageHero';
import CtaSection from '../components/landing/CtaSection';
import Reveal from '../components/landing/Reveal';
import PhoneDemo, { PHONE_MESSAGE_COUNT } from '../components/landing/PhoneDemo';
import { gsap, ScrollTrigger, prefersReducedMotion, scrollToElement } from '../components/landing/gsap';
import '../components/landing/landing.css';
import './HowItWorks.css';

const stripCheck = (s: string) => s.replace(/^✓\s*/, '');
const stripNumber = (s: string) => s.replace(/^\d+\.\s*/, '');

const HowItWorks: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const lineRef = useRef<HTMLSpanElement | null>(null);
  const phoneSectionRef = useRef<HTMLElement | null>(null);
  const [visibleMessages, setVisibleMessages] = useState(0);

  /* Ligne de la timeline qui se dessine au scroll */
  useEffect(() => {
    const wrap = timelineRef.current;
    const line = lineRef.current;
    if (!wrap || !line) return;
    if (prefersReducedMotion()) {
      line.style.transform = 'scaleY(1)';
      return;
    }
    const ctx = gsap.context(() => {
      gsap.fromTo(
        line,
        { scaleY: 0 },
        {
          scaleY: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: wrap,
            start: 'top 65%',
            end: 'bottom 65%',
            scrub: 0.6,
          },
        },
      );
    }, wrap);
    return () => ctx.revert();
  }, []);

  /* Conversation du téléphone : messages qui défilent quand la section est visible */
  useEffect(() => {
    const section = phoneSectionRef.current;
    if (!section) return;
    let interval: number | null = null;
    const st = ScrollTrigger.create({
      trigger: section,
      start: 'top 65%',
      once: true,
      onEnter: () => {
        let count = 0;
        interval = window.setInterval(() => {
          count += 1;
          setVisibleMessages(count);
          if (count >= PHONE_MESSAGE_COUNT && interval) window.clearInterval(interval);
        }, 650);
      },
    });
    return () => {
      if (interval) window.clearInterval(interval);
      st.kill();
    };
  }, []);

  const steps = [
    {
      icon: <UserAddOutlined />,
      title: stripNumber(t.how.step1Title),
      description: t.features.instantDesc,
      details: stripCheck(t.how.noCard),
      tag: t.features.instantTitle,
    },
    {
      icon: <FileAddOutlined />,
      title: stripNumber(t.how.step2Title),
      description: t.features.paymentsDesc,
      details: stripCheck(t.how.shareLink),
      tag: t.features.paymentsTitle,
    },
    {
      icon: <SendOutlined />,
      title: stripNumber(t.how.step3Title),
      description: t.features.withdrawDesc,
      details: t.how.fee,
      tag: t.features.withdrawTitle,
    },
  ];

  return (
    <>
      <SEOHead
        title={t.howPage.seoTitle}
        description={t.howPage.seoDescription}
        canonical="/how-it-works/"
        keywords="how telegram stars payment works, telegram stars payment guide, accept telegram stars payments, telegram stars payment process"
      />
      <StructuredData type="page" title={t.howPage.title} description={t.howPage.seoDescription} />

      <PublicHeader />

      <ScrollSmootherComponent smooth={1.2} effects normalizeScroll>
        <div className="ts-landing ts-how-page">
          <PageHero eyebrow={t.how.subtitle} title={t.howPage.title} subtitle={t.howPage.subtitle}>
            <button type="button" className="ts-btn ts-btn--primary" onClick={() => scrollToElement('timeline')}>
              <PlayCircleOutlined />
              {t.how.startDemo}
            </button>
          </PageHero>

          {/* Timeline verticale */}
          <section id="timeline" className="ts-section ts-timeline-section">
            <div className="ts-container">
              <div className="ts-timeline" ref={timelineRef}>
                <span className="ts-timeline-track" aria-hidden="true">
                  <span className="ts-timeline-line" ref={lineRef} />
                </span>

                {steps.map((step, i) => (
                  <Reveal key={i} className={`ts-timeline-row ${i % 2 === 1 ? 'reverse' : ''}`} y={50}>
                    <div className="ts-timeline-node">
                      <span className="ts-timeline-dot">{i + 1}</span>
                    </div>
                    <article className="ts-timeline-card">
                      <div className="ts-timeline-card-top">
                        <span className="ts-timeline-icon">{step.icon}</span>
                        <span className="ts-timeline-tag">{step.tag}</span>
                      </div>
                      <h2 className="ts-timeline-title">{step.title}</h2>
                      <p className="ts-timeline-desc">{step.description}</p>
                      <p className="ts-timeline-details">
                        <CheckCircleOutlined /> {step.details}
                      </p>
                    </article>
                    <div className="ts-timeline-spacer" aria-hidden="true" />
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* Ce que voit le client dans Telegram */}
          <section className="ts-section ts-phone-section" ref={phoneSectionRef}>
            <div className="ts-container ts-phone-grid">
              <Reveal className="ts-phone-copy" y={40}>
                <span className="ts-eyebrow ts-eyebrow--light">
                  <span className="ts-eyebrow-dot" />
                  {t.demo.botName}
                </span>
                <h2 className="ts-title ts-title--light">{t.features.paymentsTitle}</h2>
                <p className="ts-lead ts-lead--light">{t.faq.a1}</p>
                <ul className="ts-phone-points">
                  <li>
                    <CheckCircleOutlined /> {t.demo.linkCreated}
                  </li>
                  <li>
                    <CheckCircleOutlined /> {t.demo.confirmPay}
                  </li>
                  <li>
                    <CheckCircleOutlined /> {t.demo.paymentReceived}
                  </li>
                </ul>
                <button type="button" className="ts-btn ts-btn--light" onClick={() => navigate('/login')}>
                  {t.cta.createAccount}
                  <ArrowRightOutlined />
                </button>
              </Reveal>
              <Reveal className="ts-phone-wrap" y={60} scale={0.95}>
                <div className="ts-phone-glow" />
                <PhoneDemo visible={visibleMessages} typing={visibleMessages > 0 && visibleMessages < PHONE_MESSAGE_COUNT} />
              </Reveal>
            </div>
          </section>

          <CtaSection />
          <PublicFooter />
        </div>
      </ScrollSmootherComponent>
    </>
  );
};

export default HowItWorks;
